import { Box, Button, Container, Stack, Typography } from '@mui/material';
import React from 'react';
import SEO from '../components/SEO';

import FiftyFiftyCustody from '../assets/images/5050-custody.png';
import AppealableTemporaryOrdersImage from '../assets/images/appealable-temporary-orders.png';
import CamerasInCourtroom from '../assets/images/cameras-in-courtrooms.png';
import ChildSupportReform from '../assets/images/child-support-dual-calculation-reform.png';
import ChildSupportIntegrityImage from '../assets/images/child-support-integrity-act.png';
import FamilyRightsDueProcessImage from '../assets/images/family-rights-due-process-act.png';
import JusticeDelayedJusticeDenied from '../assets/images/justice-delayed-is-justice-denied.png';
import ladyJusticeImage from '../assets/images/lady-justice.webp';
import LitigantAccessEconomyBillImage from '../assets/images/litigant-access-economy-act.png';
import ParentChildRestorationImage from '../assets/images/parent-child-restoration-act.png';
import JudicialAccountabilityReferralImage from '../assets/images/judicial-accountability-referral-act.png';
import ReimbursementFeesImage from '../assets/images/reimbursement-fees-parental-alienation.png';
import ThreeStrikes from '../assets/images/three-strikes.png';
import TimeTakenTimeBack from '../assets/images/time-taken-time-back.png';
import BestInterestChildImage from '../assets/images/best-interests-child-courtroom-1000.png';
import NavigateToButton from '../components/NavigateToButton';

const Bills: React.FC = () => {
  return (
    <Container
      component="main"
      maxWidth={false}
      sx={{
        '& > .MuiStack-root h2': {
          fontSize: { xs: '2rem', md: '2.75rem' },
          fontWeight: 700,
          lineHeight: 1.15,
        },
        '& > .MuiStack-root .MuiTypography-body1': {
          fontSize: { xs: '1.125rem', md: '1.375rem' },
          lineHeight: 1.6,
        },
        '& > .MuiStack-root .MuiButton-root': {
          fontSize: { xs: '1rem', md: '1.125rem' },
          paddingX: { xs: 3, md: 4 },
          paddingY: { xs: 1.25, md: 1.5 },
        },
      }}
    >
      <SEO
        title="RobertGarza.us Family Court Reform - Bills"
        description="Explore fourteen Family Court Reform bills—comprehensive proposals to reform family courts, protect children, and advance parental equality worldwide."
        path="/family-court-reform-bills"
      />
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          padding: { xs: 0, md: 20 },
          backgroundImage: `url(${ladyJusticeImage})`,
          backgroundPosition: { xs: 'top', md: 'top left' },
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Typography variant="h1" component="h1" gutterBottom sx={{ fontWeight: '800', mb: 1 }}>
          The Family Court Reform Bill Stack
        </Typography>
        <Typography variant="h2" component="h2" gutterBottom sx={{ fontWeight: '600' }}>
          Bills that fix family court and protect our children
        </Typography>
        <Typography variant="h3" component="h3" sx={{ fontWeight: '200', lineHeight: '1.5', mb: 4 }}>
          Your voice matters. RobertGarza.us empowers you to take direct action in your state by emailing your
          representatives to express your support for bill proposals for real family law judicial reform.
        </Typography>

        <Button
          variant="contained"
          onClick={() => {
            const el = document.getElementById('bills-section');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Explore Bills
        </Button>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          paddingX: { xs: 0, md: 20 },
        }}
      >
        <Typography id="bills-section" variant="h2" component="h2" gutterBottom sx={{ fontWeight: '800', mb: 1 }}>
          The Bills
        </Typography>
        <Typography variant="h3" component="h3" gutterBottom sx={{ fontWeight: '200' }}>
          Bills that fix family court and protect our children
        </Typography>
      </Box>
      <Stack direction={{ xs: 'column', md: 'row' }} my={4} justifyContent="center" alignItems="center" spacing={2}>
        <Box
          width={{ xs: '100%', md: 800, lg: 1000 }}
          height={{ xs: 300, md: 800, lg: 1000 }}
          sx={{
            backgroundImage: `url(${FiftyFiftyCustody})`,
            backgroundPosition: { xs: 'top', md: 'center center' },
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            order: { xs: 2, md: 0 },
          }}
        ></Box>
        <Stack
          direction={'column'}
          mr={{ xs: 0, md: 4 }}
          mb={{ xs: 4, md: 0 }}
          paddingX={{ xs: 2, md: 20 }}
          alignItems="flex-start"
          textAlign="left"
          sx={{ order: { xs: 2, md: 0 } }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            A New Start at Equality
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This law establishes equal custody as the starting point, ensuring both parents have meaningful
            relationships with their children.
          </Typography>
          <NavigateToButton billSlug="a-new-start-at-equality" onClick={() => {}} sx={{ mb: { xs: 6, md: 0 } }}>
            Learn More
          </NavigateToButton>
        </Stack>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} my={4} justifyContent="center" alignItems="center" spacing={2}>
        <Stack
          direction={'column'}
          mr={{ xs: 0, md: 4 }}
          mb={{ xs: 4, md: 0 }}
          paddingX={{ xs: 2, md: 20 }}
          alignItems="flex-start"
          textAlign="left"
          sx={{ order: { xs: 1, md: 0 } }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            Time Taken Time Back
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This law protects parents from false allegations by ensuring lost custody time is restored, discouraging
            dishonest tactics and false allegations.
          </Typography>
          <NavigateToButton billSlug="time-taken-time-back" onClick={() => {}} sx={{ mb: { xs: 6, md: 0 } }}>
            Learn More
          </NavigateToButton>
        </Stack>
        <Box
          width={{ xs: '100%', md: 800, lg: 1000 }}
          height={{ xs: 300, md: 800, lg: 1000 }}
          sx={{
            backgroundImage: `url(${TimeTakenTimeBack})`,
            backgroundPosition: { xs: 'top', md: 'center center' },
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            order: { xs: 0, md: 0 },
          }}
        ></Box>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} my={4} justifyContent="center" alignItems="center" spacing={2}>
        <Box
          width={{ xs: '100%', md: 800, lg: 1000 }}
          height={{ xs: 300, md: 800, lg: 1000 }}
          sx={{
            backgroundImage: `url(${ThreeStrikes})`,
            backgroundPosition: { xs: 'top', md: 'center center' },
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            order: { xs: 0, md: 0 },
          }}
        ></Box>
        <Stack
          direction={'column'}
          mr={{ xs: 0, md: 4 }}
          mb={{ xs: 4, md: 0 }}
          padding={{ xs: 2, md: 20 }}
          alignItems="flex-start"
          textAlign="left"
          sx={{ order: { xs: 1, md: 0 } }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            Three Strikes Law
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Holds parents accountable for repeated custody interference, safeguarding the other parent’s right to
            maintain a relationship with their child.
          </Typography>
          <NavigateToButton billSlug="three-strikes" onClick={() => {}} sx={{ mb: { xs: 6, md: 0 } }}>
            Learn More
          </NavigateToButton>
        </Stack>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} my={4} justifyContent="center" alignItems="center" spacing={2}>
        <Stack
          direction={'column'}
          mr={{ xs: 0, md: 4 }}
          mb={{ xs: 4, md: 0 }}
          paddingX={{ xs: 2, md: 20 }}
          alignItems="flex-start"
          textAlign="left"
          sx={{ order: { xs: 1, md: 0 } }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            Justice Delayed is Justice Denied
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Mandatory timelines for family court proceedings prevent prolonged cases that harm children and families.
          </Typography>
          <NavigateToButton
            billSlug="justice-delayed-is-justice-denied"
            onClick={() => {}}
            sx={{ mb: { xs: 6, md: 0 } }}
          >
            Learn More
          </NavigateToButton>
        </Stack>
        <Box
          width={{ xs: '100%', md: 800, lg: 1000 }}
          height={{ xs: 300, md: 800, lg: 1000 }}
          sx={{
            backgroundImage: `url(${JusticeDelayedJusticeDenied})`,
            backgroundPosition: { xs: 'top', md: 'center center' },
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            order: { xs: 0, md: 0 },
          }}
        ></Box>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} my={4} justifyContent="center" alignItems="center" spacing={2}>
        <Box
          width={{ xs: '100%', md: 800, lg: 1000 }}
          height={{ xs: 300, md: 800, lg: 1000 }}
          sx={{
            backgroundImage: `url(${CamerasInCourtroom})`,
            backgroundPosition: { xs: 'top', md: 'center center' },
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            order: { xs: 0, md: 0 },
          }}
        ></Box>
        <Stack
          direction={'column'}
          mr={{ xs: 0, md: 4 }}
          mb={{ xs: 4, md: 0 }}
          padding={{ xs: 2, md: 20 }}
          alignItems="flex-start"
          textAlign="left"
          sx={{ order: { xs: 1, md: 0 } }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            Cameras in Courtrooms
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Mandatory recording in family courtrooms ensures transparency, protects constitutional rights, and prevents
            judicial misconduct.
          </Typography>
          <NavigateToButton billSlug="cameras-in-courtrooms" onClick={() => {}} sx={{ mb: { xs: 6, md: 0 } }}>
            Learn More
          </NavigateToButton>
        </Stack>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} my={4} justifyContent="center" alignItems="center" spacing={2}>
        <Stack
          direction={'column'}
          mr={{ xs: 0, md: 4 }}
          mb={{ xs: 4, md: 0 }}
          padding={{ xs: 2, md: 20 }}
          alignItems="flex-start"
          textAlign="left"
          sx={{ order: { xs: 1, md: 0 } }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            Child Support Dual Calculation Reform
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Reform child support calculations to be fair, transparent, and consider both parents' circumstances and
            parenting time.
          </Typography>
          <NavigateToButton
            billSlug="child-support-dual-calculation-reform"
            onClick={() => {}}
            sx={{ mb: { xs: 6, md: 0 } }}
          >
            Learn More
          </NavigateToButton>
        </Stack>
        <Box
          width={{ xs: '100%', md: 800, lg: 1000 }}
          height={{ xs: 300, md: 800, lg: 1000 }}
          sx={{
            backgroundImage: `url(${ChildSupportReform})`,
            backgroundPosition: { xs: 'top', md: 'center center' },
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            order: { xs: 0, md: 0 },
          }}
        ></Box>
      </Stack>

      {/* Litigant Access Economy Bill Section */}
      <Stack direction={{ xs: 'column', md: 'row' }} my={4} justifyContent="center" alignItems="center" spacing={2}>
        <Box
          width={{ xs: '100%', md: 800, lg: 1000 }}
          height={{ xs: 300, md: 800, lg: 1000 }}
          sx={{
            backgroundImage: `url(${LitigantAccessEconomyBillImage})`,
            backgroundPosition: { xs: 'top', md: 'center center' },
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            order: { xs: 0, md: 0 },
          }}
        ></Box>
        <Stack
          direction={'column'}
          mr={{ xs: 0, md: 4 }}
          mb={{ xs: 4, md: 0 }}
          padding={{ xs: 2, md: 20 }}
          alignItems="flex-start"
          textAlign="left"
          sx={{ order: { xs: 1, md: 0 } }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            The Litigant Access and Economy Act
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Reducing court costs and improving access to ensure all families can afford justice in family court
            proceedings.
          </Typography>
          <NavigateToButton billSlug="litigant-access-economy-act" onClick={() => {}} sx={{ mb: { xs: 6, md: 0 } }}>
            Learn More
          </NavigateToButton>
        </Stack>
      </Stack>

      {/* Appealable Temporary Orders Section */}
      <Stack direction={{ xs: 'column', md: 'row' }} my={4} justifyContent="center" alignItems="center" spacing={2}>
        <Stack
          direction={'column'}
          mr={{ xs: 0, md: 4 }}
          mb={{ xs: 4, md: 0 }}
          padding={{ xs: 2, md: 20 }}
          alignItems="flex-start"
          textAlign="left"
          sx={{ order: { xs: 1, md: 0 } }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            Appealable Temporary Orders
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Allowing appeals of temporary orders ensures due process and prevents irreversible harm from rushed
            decisions.
          </Typography>
          <NavigateToButton billSlug="appealable-temporary-orders" onClick={() => {}} sx={{ mb: { xs: 6, md: 0 } }}>
            Learn More
          </NavigateToButton>
        </Stack>
        <Box
          width={{ xs: '100%', md: 800, lg: 1000 }}
          height={{ xs: 300, md: 800, lg: 1000 }}
          sx={{
            backgroundImage: `url(${AppealableTemporaryOrdersImage})`,
            backgroundPosition: { xs: 'top', md: 'center center' },
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            order: { xs: 0, md: 0 },
          }}
        ></Box>
      </Stack>

      {/* Reimbursement of Fees for Parental Alienation */}
      <Stack direction={{ xs: 'column', md: 'row' }} my={4} justifyContent="center" alignItems="center" spacing={2}>
        <Stack
          direction={'column'}
          mr={{ xs: 0, md: 4 }}
          mb={{ xs: 4, md: 0 }}
          padding={{ xs: 2, md: 20 }}
          alignItems="flex-start"
          textAlign="left"
          sx={{ order: { xs: 1, md: 0 } }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            Reimbursement of Fees for Parental Alienating Behaviors
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Parents proven to engage in alienating behaviors should reimburse the other parent's legal fees and costs
            incurred defending against false allegations.
          </Typography>
          <NavigateToButton
            billSlug="reimbursement-fees-parental-alienation"
            onClick={() => {}}
            sx={{ mb: { xs: 6, md: 0 } }}
          >
            Learn More
          </NavigateToButton>
        </Stack>
        <Box
          width={{ xs: '100%', md: 800, lg: 1000 }}
          height={{ xs: 300, md: 800, lg: 1000 }}
          sx={{
            backgroundImage: `url(${ReimbursementFeesImage})`,
            backgroundPosition: { xs: 'top', md: 'center center' },
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            order: { xs: 0, md: 1 },
          }}
        ></Box>
      </Stack>

      {/* The Family Rights and Due Process Act */}
      <Stack direction={{ xs: 'column', md: 'row' }} my={4} justifyContent="center" alignItems="center" spacing={2}>
        <Box
          width={{ xs: '100%', md: 800, lg: 1000 }}
          height={{ xs: 300, md: 800, lg: 1000 }}
          sx={{
            backgroundImage: `url(${FamilyRightsDueProcessImage})`,
            backgroundPosition: { xs: 'top', md: 'center center' },
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            order: { xs: 0, md: 0 },
          }}
        ></Box>
        <Stack
          direction={'column'}
          mr={{ xs: 0, md: 4 }}
          mb={{ xs: 4, md: 0 }}
          padding={{ xs: 2, md: 20 }}
          alignItems="flex-start"
          textAlign="left"
          sx={{ order: { xs: 1, md: 0 } }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            The Family Rights and Due Process Act
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Establishes comprehensive protections for fundamental constitutional rights in family court proceedings,
            ensuring due process and equal protection under the law.
          </Typography>
          <NavigateToButton billSlug="family-rights-due-process-act" onClick={() => {}} sx={{ mb: { xs: 6, md: 0 } }}>
            Learn More
          </NavigateToButton>
        </Stack>
      </Stack>

      {/* Child Support Integrity Act */}
      <Stack direction={{ xs: 'column', md: 'row' }} my={4} justifyContent="center" alignItems="center" spacing={2}>
        <Stack
          direction={'column'}
          mr={{ xs: 0, md: 4 }}
          mb={{ xs: 4, md: 0 }}
          padding={{ xs: 2, md: 20 }}
          alignItems="flex-start"
          textAlign="left"
          sx={{ order: { xs: 1, md: 0 } }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            Child Support Integrity Act
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This legislation establishes DNA testing rights and transparency in child support enforcement, ensuring
            biological accuracy and fair practices for all families.
          </Typography>
          <NavigateToButton billSlug="child-support-integrity-act" onClick={() => {}} sx={{ mb: { xs: 6, md: 0 } }}>
            Learn More
          </NavigateToButton>
        </Stack>
        <Box
          width={{ xs: '100%', md: 800, lg: 1000 }}
          height={{ xs: 300, md: 800, lg: 1000 }}
          sx={{
            backgroundImage: `url(${ChildSupportIntegrityImage})`,
            backgroundPosition: { xs: 'top', md: 'center center' },
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            order: { xs: 0, md: 1 },
          }}
        ></Box>
      </Stack>
      {/* Best Interest of the Child Definition Act */}
      <Stack direction={{ xs: 'column', md: 'row' }} my={4} justifyContent="center" alignItems="center" spacing={2}>
        <Box
          width={{ xs: '100%', md: 800, lg: 1000 }}
          height={{ xs: 300, md: 800, lg: 1000 }}
          sx={{
            backgroundImage: `url(${BestInterestChildImage})`,
            backgroundPosition: { xs: 'top', md: 'center center' },
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            order: { xs: 0, md: 0 },
          }}
        ></Box>
        <Stack
          direction={'column'}
          mr={{ xs: 0, md: 4 }}
          mb={{ xs: 4, md: 0 }}
          padding={{ xs: 2, md: 20 }}
          alignItems="flex-start"
          textAlign="left"
          sx={{ order: { xs: 1, md: 0 } }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            Best Interest of the Child Definition Act
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This legislation establishes a clear statutory definition of "best interest of the child," ending arbitrary
            judicial interpretations and ensuring consistent, fair custody determinations for all families.
          </Typography>
          <NavigateToButton
            billSlug="best-interest-of-the-child-definition-act"
            onClick={() => {}}
            sx={{ mb: { xs: 6, md: 0 } }}
          >
            Learn More
          </NavigateToButton>
        </Stack>
      </Stack>
      {/* Parent-Child Restoration Act */}
      <Stack direction={{ xs: 'column', md: 'row' }} my={4} justifyContent="center" alignItems="center" spacing={2}>
        <Stack
          direction={'column'}
          mr={{ xs: 0, md: 4 }}
          mb={{ xs: 4, md: 0 }}
          padding={{ xs: 2, md: 20 }}
          alignItems="flex-start"
          textAlign="left"
          sx={{ order: { xs: 1, md: 0 } }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            Parent-Child Restoration Act
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            When allegations that caused temporary custody or visitation restrictions are not substantiated, this act
            requires courts to promptly restore the prior parenting order and award compensatory parenting time—while
            preserving necessary child protections.
          </Typography>
          <NavigateToButton billSlug="parent-child-restoration-act" onClick={() => {}} sx={{ mb: { xs: 6, md: 0 } }}>
            Learn More
          </NavigateToButton>
        </Stack>
        <Box
          width={{ xs: '100%', md: 800, lg: 1000 }}
          height={{ xs: 300, md: 800, lg: 1000 }}
          role="img"
          aria-label="Parent and child reunited beside broken chains and scales of justice, representing the Parent-Child Restoration Act"
          sx={{
            backgroundImage: `url(${ParentChildRestorationImage})`,
            backgroundPosition: 'center center',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            order: { xs: 0, md: 1 },
          }}
        />
      </Stack>
      {/* Judicial Accountability Referral Act */}
      <Stack direction={{ xs: 'column', md: 'row' }} my={4} justifyContent="center" alignItems="center" spacing={2}>
        <Box
          width={{ xs: '100%', md: 800, lg: 1000 }}
          height={{ xs: 300, md: 800, lg: 1000 }}
          role="img"
          aria-label="Family dismissed by a judge while an appellate clerk refers an opinion to independent commissioners, representing the Judicial Accountability Referral Act"
          sx={{
            backgroundImage: `url(${JudicialAccountabilityReferralImage})`,
            backgroundPosition: 'center center',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            order: { xs: 0, md: 0 },
          }}
        />
        <Stack
          direction={'column'}
          mr={{ xs: 0, md: 4 }}
          mb={{ xs: 4, md: 0 }}
          padding={{ xs: 2, md: 20 }}
          alignItems="flex-start"
          textAlign="left"
          sx={{ order: { xs: 1, md: 0 } }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            Judicial Accountability Referral Act
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            When families are dismissed without a meaningful hearing, this proposed act creates a referral pathway that
            brings judicial conduct concerns from family court proceedings before an independent review commission—so
            accountability does not end at the courtroom door.
          </Typography>
          <NavigateToButton
            billSlug="judicial-accountability-referral-act"
            onClick={() => {}}
            sx={{ mb: { xs: 6, md: 0 } }}
          >
            Learn More
          </NavigateToButton>
        </Stack>
      </Stack>
    </Container>
  );
};

export default Bills;
